<template>
  <teleport to="body">
    <div v-if="isOpen" class="plp-upload-modal-overlay" @click.self="handleClose">
      <div class="plp-upload-modal">
        <!-- Close button -->
        <button class="plp-upload-modal__close" @click="handleClose" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <div class="plp-upload-modal__content">
          <!-- Header -->
          <h2 class="plp-upload-modal__title">Select a file to upload</h2>

          <!-- Primary: Computer Upload -->
          <button
            class="plp-upload-modal__choose-file-btn"
            :class="{ 'is-loading': uploading }"
            :disabled="uploading"
            @click="triggerFileUpload"
          >
            <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M16 19H2V5H11V3H2C0.9 3 0 3.9 0 5V19C0 20.1 0.9 21 2 21H16C17.1 21 18 20.1 18 19V10H16V19ZM8.21 15.83L6.25 13.47L3.5 17H14.5L10.96 12.29L8.21 15.83ZM18 3V0H16V3H13C13.01 3.01 13 5 13 5H16V7.99C16.01 8 18 7.99 18 7.99V5H21V3H18Z"
                fill="currentColor"
              />
            </svg>
            <span>{{ uploading ? 'Uploading...' : 'Choose File' }}</span>
          </button>

          <!-- Upload Error -->
          <div v-if="uploadError" class="plp-upload-modal__upload-error">
            <img src="@/svg/icon/upload-error.svg" alt="Upload Error">
            <span>{{ uploadError }}</span>
          </div>

          <!-- Divider -->
          <div class="plp-upload-modal__divider">
            <span class="plp-upload-modal__divider-line"></span>
            <span class="plp-upload-modal__divider-text">or</span>
            <span class="plp-upload-modal__divider-line"></span>
          </div>

          <!-- Secondary: QR Upload -->
          <div class="plp-upload-modal__qr-section">
            <h3 class="plp-upload-modal__qr-title">Upload from your phone</h3>
            <p class="plp-upload-modal__qr-description">
              Scan the QR code to upload from any mobile device and keep this window open while your photo is uploading.
            </p>

            <!-- QR Code -->
            <div class="plp-upload-modal__qr-wrapper">
              <div v-if="pollTimedOut" class="plp-upload-modal__qr-error">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#E53935"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <p>QR code expired. Please try again.</p>
                <button class="plp-upload-modal__retry-btn" @click="regenerateQRCode">Generate New Code</button>
              </div>
              <template v-else>
                <canvas v-show="qrLoaded" ref="qrCanvas" class="plp-upload-modal__qr-canvas"></canvas>
                <div v-show="!qrLoaded" class="plp-upload-modal__spinner">
                  <svg
                    class="spinner"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="12" cy="12" r="10" stroke="#ddd" stroke-width="3" />
                    <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="#333" stroke-width="3" stroke-linecap="round" />
                  </svg>
                </div>
              </template>
            </div>
          </div>

          <!-- 3-Step Guide -->
          <div class="plp-upload-modal__step-guide">
            <div class="plp-upload-modal__step">
              <div class="plp-upload-modal__step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="7" height="7" stroke="currentColor" stroke-width="2" />
                  <rect x="14" y="3" width="7" height="7" stroke="currentColor" stroke-width="2" />
                  <rect x="3" y="14" width="7" height="7" stroke="currentColor" stroke-width="2" />
                  <rect x="14" y="14" width="7" height="7" stroke="currentColor" stroke-width="2" />
                </svg>
              </div>
              <p class="plp-upload-modal__step-text">Scan the QR code using the camera on your phone</p>
            </div>
            <div class="plp-upload-modal__step-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="plp-upload-modal__step">
              <div class="plp-upload-modal__step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" stroke-width="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                  <path
                    d="M21 15L16 10L4 21"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <p class="plp-upload-modal__step-text">Select the photo you'd like to frame</p>
            </div>
            <div class="plp-upload-modal__step-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="plp-upload-modal__step">
              <div class="plp-upload-modal__step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="2" />
                  <path d="M8 21H16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                  <path d="M12 17V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
              </div>
              <p class="plp-upload-modal__step-text">Your photo will appear on the screen once uploaded</p>
            </div>
          </div>

          <!-- Hidden file input -->
          <input
            v-show="false"
            ref="fileField"
            type="file"
            name="plp-upload"
            label="Upload Image"
            accept="image/gif, image/jpeg, image/png, image/heic"
            @change="handleFileSelected($event)"
          />
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { v4 as uuid } from 'uuid';
import { add as addToDataLayer, EVENTS } from '@framebridge/toolbox/../app/javascript/domains/DataLayer.js';
import artworkUploader from '@framebridge/toolbox/apihelpers/artworkUploader';
import GlazeClient from '@framebridge/toolbox/apihelpers/glazeClient';
import FrameSpec from '@framebridge/toolbox/FrameSpec';
import { setArtworkSizeDefaults } from '@framebridge/toolbox/artwork';
import { normalizedArtworkData } from '@framebridge/toolbox/framingUtilities.js';
import { heicToJpeg } from '@framebridge/toolbox/heicConverter';
import { getImageErrorsForFile } from '@framebridge/toolbox/validateImageFile.js';
import QRCode from 'qrcode';

export default {
  name: 'PLPUploadModal',
  emits: ['close', 'upload-complete', 'upload-error', 'upload-initiated', 'upload-progress'],
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    joineryHost: {
      type: String,
      required: true,
    },
    shopifyHost: {
      type: String,
      default: '',
    },
    conveyance: {
      type: String,
      default: 'uploaded',
    },
  },
  data() {
    return {
      frameSpec: null,
      qrUrl: null,
      qrLoaded: false,
      pollTimedOut: false,
      pollingInterval: null,
      uploadError: null,
      uploading: false,
    };
  },
  computed: {
    GlazeClient() {
      return new GlazeClient(null, { baseUrl: this.joineryHost });
    },
    resolvedShopifyHost() {
      return this.shopifyHost || window.location.origin;
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.initQRCode();
        document.body.style.overflow = 'hidden';
      } else {
        this.stopPolling();
        document.body.style.overflow = '';
      }
    },
  },
  beforeUnmount() {
    this.stopPolling();
    document.body.style.overflow = '';
  },
  methods: {
    async initQRCode() {
      try {
        this.qrLoaded = false;
        this.pollTimedOut = false;
        const url = await this.generateUrlForQRCode();
        this.qrUrl = url;

        // Wait for next tick to ensure canvas is rendered
        await this.$nextTick();
        await this.generateQRCode(url);

        const timeStamp = await this.frameSpec.fetchUpdatedAt();
        this.pollForUpdatedFrameSpec(timeStamp, this.frameSpec);
      } catch (error) {
        console.error('Error initializing QR code:', error);
      }
    },
    async generateUrlForQRCode() {
      this.frameSpec = await FrameSpec.build({
        joineryHost: this.joineryHost,
        shopifyHost: this.resolvedShopifyHost,
        templateOptions: { withArtwork: true },
      });

      await this.frameSpec.saveToServer();

      return `${this.resolvedShopifyHost}/apps/designer/framing-flow/upload-qr?frame-spec=${this.frameSpec.number}&auth-token=${this.frameSpec.authorizationToken}`;
    },
    async generateQRCode(url) {
      try {
        const canvas = this.$refs.qrCanvas;
        if (canvas) {
          await QRCode.toCanvas(canvas, url, {
            width: 200,
            margin: 0,
          });
          this.qrLoaded = true;
        }
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    },
    pollForUpdatedFrameSpec(initialTimeStamp, frameSpec) {
      let pollCount = 0;
      const maxPolls = 1000;

      this.pollingInterval = setInterval(async () => {
        try {
          pollCount++;
          if (pollCount >= maxPolls) {
            console.error('Polling limit reached. Stopping polling.');
            this.stopPolling();
            this.pollTimedOut = true;
            return;
          }

          const updatedTimeStamp = await frameSpec.fetchUpdatedAt();
          if (updatedTimeStamp !== initialTimeStamp) {
            this.stopPolling();
            await frameSpec.updateFromServer();

            // Track QR upload analytics
            addToDataLayer({
              event: EVENTS.PHOTO_UPLOADED,
              method: 'qr',
              conveyance: this.conveyance,
              context: 'plp',
            });

            this.$emit('upload-complete', {
              artwork: frameSpec.artworks[0],
              method: 'qr',
            });
          }
        } catch (error) {
          console.error('Error during polling:', error);
          this.stopPolling();
        }
      }, 1000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async regenerateQRCode() {
      this.pollTimedOut = false;
      this.qrLoaded = false;
      await this.initQRCode();
    },
    handleClose() {
      this.stopPolling();
      this.$emit('close');
    },
    triggerFileUpload() {
      this.$refs.fileField.value = null;
      this.$refs.fileField.click();
    },
    async handleFileSelected(event) {
      this.uploading = true;
      this.uploadError = null;

      let files = event.target?.files;

      if (!files || !files[0]) {
        this.uploading = false;
        return;
      }

      // Convert HEIC if needed
      if (files[0].type.includes('heic')) {
        try {
          const convertedFile = await this.convertHeicToJpeg(files[0]);
          files = [convertedFile];
        } catch (error) {
          this.uploadError = 'Error converting HEIC to JPEG';
          this.$emit('upload-error', 'Error converting HEIC to JPEG');
          this.uploading = false;
          return;
        }
      }

      const file = files[0];
      const reader = new FileReader();

      try {
        const value = await getImageErrorsForFile(file, reader, true, 0, 0);

        const upload = {
          uuid: uuid(),
          groupId: uuid(),
          startedAt: new Date(),
          file,
        };

        this.$emit('upload-initiated', upload);

        addToDataLayer({
          event: EVENTS.UPLOAD_STARTED,
          startedAt: upload.startedAt.toISOString(),
          method: 'click',
          context: 'plp',
        });

        const response = await artworkUploader({
          file,
          client: this.GlazeClient,
          artwork: {
            convex_metadata: value,
            use_convex: true,
          },
          onUploadProgress: (event) => {
            this.$emit('upload-progress', event);
          },
          onArtworkCreate: (artwork) => {
            upload.processingStartedAt = new Date();
            addToDataLayer({
              event: EVENTS.PROCESSING_STARTED,
              startedAt: upload.processingStartedAt.toISOString(),
              context: 'plp',
            });
          },
        });

        upload.processingFinishedAt = new Date();

        addToDataLayer({
          event: EVENTS.PROCESSING_FINISHED,
          startedAt: upload.processingStartedAt?.toISOString(),
          finishedAt: upload.processingFinishedAt.toISOString(),
          duration: upload.processingFinishedAt - (upload.processingStartedAt || upload.startedAt),
          context: 'plp',
        });

        addToDataLayer({
          event: EVENTS.PHOTO_UPLOADED,
          method: 'click',
          conveyance: this.conveyance,
          context: 'plp',
        });

        upload.artwork = setArtworkSizeDefaults(normalizedArtworkData(response.data), {
          sizeDefault: 'minForSizeGroup',
          sizeGroup: 'S',
        });

        this.$emit('upload-complete', { ...upload, method: 'click' });
        this.uploading = false;
      } catch (reason) {
        this.uploadError = `${reason || 'Unknown error'}`;
        this.$emit('upload-error', reason);
        this.uploading = false;
        addToDataLayer({
          event: EVENTS.UPLOAD_ERRORED,
          reason: reason?.toString(),
          method: 'click',
          conveyance: this.conveyance,
          context: 'plp',
        });
      }
    },
    async convertHeicToJpeg(file) {
      const blob = await heicToJpeg(file);
      return new File([blob], 'artwork.jpg', { type: 'image/jpeg' });
    },
  },
};
</script>

<style lang="scss">
.plp-upload-modal-overlay {
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 9999;
}

.plp-upload-modal {
  background: #fff9f4;
  border-radius: 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-height: 80vh;
  max-width: 750px;
  overflow-y: auto;
  position: relative;
  width: 90%;

  &__close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    position: absolute;
    right: 12px;
    top: 12px;
    z-index: 1;

    &:hover {
      opacity: 0.7;
    }
  }

  &__content {
    align-items: center;
    display: flex;
    flex-direction: column;
    padding: 40px 50px;
    text-align: center;

    @media screen and (max-width: 768px) {
      padding: 30px 20px;
    }
  }

  &__title {
    font-family: 'Cheltenham', Georgia, serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
    margin-bottom: 24px;

    @media screen and (min-width: 768px) {
      font-size: 28px;
    }
  }

  &__choose-file-btn {
    align-items: center;
    background: #eee171;
    border: none;
    color: #000;
    cursor: pointer;
    display: flex;
    font-family: 'Apercu', sans-serif;
    font-size: 16px;
    font-weight: 700;
    gap: 8px;
    justify-content: center;
    margin-bottom: 20px;
    max-width: 280px;
    padding: 16px 40px;
    transition: background-color 0.2s;
    width: 100%;

    &:hover:not(:disabled) {
      background: #000;
      color: #fff;

      svg path {
        fill: #fff;
      }
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    &.is-loading {
      pointer-events: none;
    }
  }

  &__upload-error {
    align-items: center;
    color: #d1533a;
    display: flex;
    font-family: 'Apercu', sans-serif;
    font-size: 14px;
    gap: 4px;
    justify-content: center;
    letter-spacing: 0.28px;
    line-height: 24px;
    margin-bottom: 16px;
    text-align: center;
    width: 100%;

    img {
      flex-shrink: 0;
      height: 24px;
      width: 24px;
    }
  }

  &__divider {
    align-items: center;
    display: flex;
    margin: 16px 0;
    width: 100%;
  }

  &__divider-line {
    background: #bbb;
    flex: 1;
    height: 1px;
  }

  &__divider-text {
    color: #666;
    font-family: 'Apercu', sans-serif;
    font-size: 14px;
    margin: 0 16px;
  }

  &__qr-section {
    margin-top: 16px;
    width: 100%;
  }

  &__qr-title {
    font-family: 'Apercu', sans-serif;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;

    @media screen and (min-width: 768px) {
      font-size: 20px;
    }
  }

  &__qr-description {
    color: #666;
    font-family: 'Apercu', sans-serif;
    font-size: 14px;
    line-height: 1.5;
    margin-bottom: 20px;
  }

  &__qr-wrapper {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 200px;
  }

  &__qr-canvas {
    border: 1px solid #eee;
  }

  &__spinner {
    .spinner {
      animation: spin 1s linear infinite;
    }
  }

  &__qr-error {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;

    p {
      color: #e53935;
      font-family: 'Apercu', sans-serif;
      font-size: 14px;
      margin: 0;
    }
  }

  &__retry-btn {
    background: none;
    border: none;
    color: #000;
    cursor: pointer;
    font-family: 'Apercu', sans-serif;
    font-size: 14px;
    font-weight: 700;
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }

  &__step-guide {
    align-items: flex-start;
    border-top: 1px solid #eee;
    display: flex;
    gap: 8px;
    justify-content: center;
    margin-top: 30px;
    padding-top: 24px;
    width: 100%;

    @media screen and (max-width: 768px) {
      align-items: center;
      flex-direction: column;
      gap: 16px;
    }
  }

  &__step {
    align-items: center;
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    max-width: 140px;
    text-align: center;
  }

  &__step-icon {
    align-items: center;
    border: 1px solid #ddd;
    border-radius: 4px;
    display: flex;
    height: 40px;
    justify-content: center;
    width: 40px;
  }

  &__step-text {
    color: #666;
    font-family: 'Apercu', sans-serif;
    font-size: 12px;
    line-height: 1.4;
    margin: 0;
  }

  &__step-arrow {
    align-items: center;
    color: #999;
    display: flex;
    height: 40px;

    @media screen and (max-width: 768px) {
      display: none;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
